/**
 * Map generation failure messages to repo source locations for operator debugging.
 * Consumed by GenerationErrorSourceLink in report-generation UI surfaces.
 */

export interface GenerationErrorSource {
  /** Repo-relative path, e.g. `src/lib/audit/providerPrecedenceVerifier.ts`. */
  file: string;
  line: number;
  /** Short label for the link text. */
  label: string;
  /** Optional one-line fix hint. */
  hint?: string;
}

const GITHUB_BLOB_BASE =
  'https://github.com/williamtheron-deep/Deep-Atomic-Platform/blob/main';

interface ErrorSourceRule {
  test: (message: string) => boolean;
  source: GenerationErrorSource;
}

const RULES: readonly ErrorSourceRule[] = [
  {
    test: (m) => /coverage gate|provider coverage|blocked by provider/i.test(m),
    source: {
      file: 'src/lib/audit/providerCoverageGate.ts',
      line: 1,
      label: 'Provider coverage gate',
      hint: 'Required providers did not return usable data — check dispatch waivers or SED section status.',
    },
  },
  {
    test: (m) => /citation density|CitationDensityGate/i.test(m),
    source: {
      file: 'src/lib/audit/citationDensityGate.ts',
      line: 1,
      label: 'Citation density gate',
      hint: 'Too many uncited claims — lower threshold, regen with focus, or allow override per runbook.',
    },
  },
  {
    test: (m) => /intent.*sed|IntentSedMismatch/i.test(m),
    source: {
      file: 'src/lib/audit/intentSedReconciler.ts',
      line: 1,
      label: 'Intent / SED reconciler',
      hint: 'Project intent conflicts with persisted SED — reconcile in wizard or update site intent.',
    },
  },
  {
    test: (m) => /site state required|SiteStateRequired/i.test(m),
    source: {
      file: 'src/lib/detectConflicts.ts',
      line: 1,
      label: 'Site state required',
      hint: 'Conflict detection needs site boundary / state fields that are missing on this site.',
    },
  },
  {
    test: (m) => /text\.trim is not a function/i.test(m),
    source: {
      file: 'src/lib/citationDensity.ts',
      line: 65,
      label: 'Citation density prose guard',
      hint: 'A non-string value reached splitSentences — often a structured custom-brief executiveSummary in degraded mode.',
    },
  },
  {
    test: (m) => /RESOURCE_PROJECT_INVALID|Invalid resource field value/i.test(m),
    source: {
      file: 'server.ts',
      line: 1032,
      label: 'Vertex gemini proxy',
      hint: 'Local GENAI_BACKEND=vertex but ADC lacks access to GOOGLE_CLOUD_PROJECT — switch to aistudio + GEMINI_API_KEY or fix gcloud project / aiplatform.user.',
    },
  },
  {
    test: (m) => /Could not load or create a Site Evidence Document/i.test(m),
    source: {
      file: 'src/lib/reportGeneration/runTypedReportGeneration.ts',
      line: 212,
      label: 'SED bootstrap',
      hint: 'SED load/bootstrap failed — check data providers and sedBootstrap wiring.',
    },
  },
  {
    test: (m) => /^Site not found$/i.test(m.trim()),
    source: {
      file: 'src/lib/reportGeneration/runTypedReportGeneration.ts',
      line: 151,
      label: 'Site lookup',
    },
  },
];

/** Resolve a repo source pointer from a generation error message. */
export function resolveGenerationErrorSource(message: string | null | undefined): GenerationErrorSource | null {
  if (!message?.trim()) return null;
  const normalized = message.trim();
  for (const rule of RULES) {
    if (rule.test(normalized)) return rule.source;
  }
  return null;
}

/** GitHub blob URL with line anchor for the resolved source. */
export function generationErrorSourceUrl(source: GenerationErrorSource): string {
  return `${GITHUB_BLOB_BASE}/${source.file}#L${source.line}`;
}

/** Display path for copy / Cmd+P, e.g. `src/lib/audit/providerPrecedenceVerifier.ts:298`. */
export function generationErrorSourcePath(source: GenerationErrorSource): string {
  return `${source.file}:${source.line}`;
}
